// =============================================================================
// trigger-service.ts — pure-function reactive trigger state slice
//
// WEB.4.E: Passive trigger registry for cards that react to other players'
// effects targeting them (Counterattack, Disaster Relief Fund,
// Petrodollar Trade Pact).
//
// Design:
//   TriggerInstance — one registered passive trigger entry.
//   TriggerState    — collection of active triggers (frozen array).
//
// Key behaviors:
//   registerTrigger   — adds a new TriggerInstance.
//   discardTrigger    — removes entry by instanceId.
//   findMatching      — finds triggers for a given (ownerSeat, event).
//   consumeCharges    — decrements chargesRemaining; removes when exhausted.
//
// Fire-points (in effect-kind-resolver-registry.ts):
//   AfterStealEnergyTargetingMe    — resolveStealEnergy Applied → victim seat
//   AfterFacilityDestroyedMine     — resolveDestroy Applied → owner seat
//   AfterFacilityDecommissionedMine — resolveDecommission Applied → owner seat
//   AfterTollPaidByOpponentToMe    — applyTollAuras applied (called externally)
//
// Pipeline integration (effect-pipeline.ts):
//   After resolver returns Applied AND afterApplyHook:
//     fireTriggersForEvent(event, ownerSeat, sourceSeat, services, log)
//   Recursion guard: EffectContext.triggerDepth >= MAX_TRIGGER_DEPTH → skip
//   MAX_TRIGGER_DEPTH = 1 (prevents Counterattack → Counterattack loop).
//
// Pure-function discipline: NO module-level state, NO singletons. All state
// threaded as explicit arguments and returned in results. Deep-frozen.
// =============================================================================

import type { CardEffect, TriggerEvent } from "@gdg/shared";
import { deepFreeze } from "../_freeze.js";
import type { ActionLog, JsonValue } from "../rng.js";
import { assertFiniteInt } from "./_validation.js";

// Re-export TriggerEvent from shared so callers can import from either location.
export type { TriggerEvent };

// ---------------------------------------------------------------------------
// Sentinel for "permanent" trigger (infinite charges)
// ---------------------------------------------------------------------------

export const TRIGGER_PERMANENT_CHARGES = 999;

// ---------------------------------------------------------------------------
// Domain types
// ---------------------------------------------------------------------------

/**
 * One active passive trigger entry.
 *
 * instanceId         — monotonic ID (caller-supplied, same counter as CIS).
 * ownerSeat          — seat that registered this trigger (their card).
 * triggerOn          — the event kind that fires this trigger.
 * response           — effects to run when trigger fires (each resolved via
 *                      runSingleEffect with swapped ctx: ownerSeat=sourceSeat,
 *                      victimSeat=originalSourceSeat).
 * chargesRemaining   — TRIGGER_PERMANENT_CHARGES = permanent; otherwise N uses.
 * sourceCardName     — for log attribution.
 */
export interface TriggerInstance {
  readonly instanceId: number;
  readonly ownerSeat: number;
  readonly triggerOn: TriggerEvent;
  readonly response: ReadonlyArray<CardEffect>;
  readonly chargesRemaining: number;
  readonly sourceCardName: string;
}

export interface TriggerState {
  readonly entries: ReadonlyArray<TriggerInstance>;
  /** Monotonic counter for auto-assigning instanceIds. */
  readonly nextId: number;
}

// ---------------------------------------------------------------------------
// Typed error
// ---------------------------------------------------------------------------

export class TriggerError extends Error {
  readonly code: "INVALID_SEAT" | "INVALID_CHARGES" | "EMPTY_RESPONSE" | "NOT_FOUND";

  constructor(code: TriggerError["code"], message: string) {
    super(message);
    this.name = "TriggerError";
    this.code = code;
  }
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createTriggerState(): TriggerState {
  return deepFreeze({ entries: [], nextId: 1 });
}

// ---------------------------------------------------------------------------
// registerTrigger
// ---------------------------------------------------------------------------

/** Input type for registerTrigger — instanceId is auto-assigned from state.nextId. */
export type TriggerInstanceInput = Omit<TriggerInstance, "instanceId">;

/**
 * Register a new TriggerInstance into state.
 * instanceId is auto-assigned from state.nextId (monotonic counter).
 * Validates: ownerSeat >= 0, chargesRemaining > 0, response non-empty.
 * No dedupe — each card play creates a distinct entry.
 *
 * Returns the assigned instanceId in the result for caller reference.
 */
export function registerTrigger(
  state: TriggerState,
  log: ActionLog,
  input: TriggerInstanceInput,
): { newState: TriggerState; log: ActionLog; instanceId: number } {
  assertFiniteInt(input.ownerSeat, "registerTrigger ownerSeat");
  if (input.ownerSeat < 0) {
    throw new TriggerError(
      "INVALID_SEAT",
      `registerTrigger: ownerSeat must be >= 0, got ${input.ownerSeat}`,
    );
  }
  if (
    !Number.isFinite(input.chargesRemaining) ||
    input.chargesRemaining <= 0 ||
    !Number.isInteger(input.chargesRemaining)
  ) {
    throw new TriggerError(
      "INVALID_CHARGES",
      `registerTrigger: chargesRemaining must be a positive integer, got ${input.chargesRemaining}`,
    );
  }
  if (input.response.length === 0) {
    throw new TriggerError(
      "EMPTY_RESPONSE",
      "registerTrigger: response must have at least one effect",
    );
  }

  const instanceId = state.nextId;
  const instance: TriggerInstance = deepFreeze({ ...input, instanceId });
  const newEntries = Object.freeze([...state.entries, instance]);
  const newState: TriggerState = deepFreeze({ entries: newEntries, nextId: instanceId + 1 });

  const nextLog = log.append({
    kind: "trigger.registered",
    data: {
      instanceId,
      ownerSeat: input.ownerSeat,
      triggerOn: input.triggerOn,
      chargesRemaining: input.chargesRemaining,
      sourceCardName: input.sourceCardName,
      responseEffectCount: input.response.length,
    } as JsonValue,
  });

  return { newState, log: nextLog, instanceId };
}

// ---------------------------------------------------------------------------
// discardTrigger
// ---------------------------------------------------------------------------

/**
 * Remove a trigger entry by instanceId.
 * Returns removed=false (with warning) when instanceId not found.
 */
export function discardTrigger(
  state: TriggerState,
  log: ActionLog,
  instanceId: number,
): { newState: TriggerState; log: ActionLog; removed: boolean } {
  assertFiniteInt(instanceId, "discardTrigger instanceId");

  const idx = state.entries.findIndex((e) => e.instanceId === instanceId);
  if (idx === -1) {
    console.warn(`[TriggerService] discardTrigger: instanceId=${instanceId} not found.`);
    return { newState: state, log, removed: false };
  }

  const removed = state.entries[idx];
  const newEntries = Object.freeze([
    ...state.entries.slice(0, idx),
    ...state.entries.slice(idx + 1),
  ]);
  const newState: TriggerState = deepFreeze({ entries: newEntries, nextId: state.nextId });

  const nextLog = log.append({
    kind: "trigger.discarded",
    data: {
      instanceId,
      ownerSeat: removed?.ownerSeat ?? -1,
      sourceCardName: removed?.sourceCardName ?? "?",
    } as JsonValue,
  });

  return { newState, log: nextLog, removed: true };
}

// ---------------------------------------------------------------------------
// findMatching
// ---------------------------------------------------------------------------

/**
 * Find all active triggers matching (ownerSeat, event).
 * Returns triggers in order of registration (FIFO).
 * Does NOT consume charges — caller must call consumeCharge after firing.
 */
export function findMatching(
  state: TriggerState,
  event: TriggerEvent,
  ownerSeat: number,
): ReadonlyArray<TriggerInstance> {
  assertFiniteInt(ownerSeat, "findMatching ownerSeat");
  return Object.freeze(
    state.entries.filter((e) => e.ownerSeat === ownerSeat && e.triggerOn === event),
  );
}

// ---------------------------------------------------------------------------
// consumeCharge
// ---------------------------------------------------------------------------

/**
 * Decrement chargesRemaining for a given instanceId.
 * Removes entry when chargesRemaining reaches 0.
 * Returns { newState, log, exhausted }.
 */
export function consumeCharge(
  state: TriggerState,
  log: ActionLog,
  instanceId: number,
): { newState: TriggerState; log: ActionLog; exhausted: boolean } {
  assertFiniteInt(instanceId, "consumeCharge instanceId");

  const idx = state.entries.findIndex((e) => e.instanceId === instanceId);
  if (idx === -1) {
    throw new TriggerError("NOT_FOUND", `consumeCharge: instanceId=${instanceId} not found`);
  }

  const entry = state.entries[idx];
  if (entry === undefined) {
    throw new TriggerError("NOT_FOUND", `consumeCharge: entry at idx=${idx} is undefined`);
  }
  const newCharges = entry.chargesRemaining - 1;
  const exhausted = newCharges <= 0;

  let newEntries: ReadonlyArray<TriggerInstance>;
  if (exhausted) {
    newEntries = Object.freeze([...state.entries.slice(0, idx), ...state.entries.slice(idx + 1)]);
  } else {
    const updated: TriggerInstance = deepFreeze({ ...entry, chargesRemaining: newCharges });
    newEntries = Object.freeze([
      ...state.entries.slice(0, idx),
      updated,
      ...state.entries.slice(idx + 1),
    ]);
  }

  const newState: TriggerState = deepFreeze({ entries: newEntries, nextId: state.nextId });

  const nextLog = log.append({
    kind: "trigger.charge-consumed",
    data: {
      instanceId,
      ownerSeat: entry.ownerSeat,
      sourceCardName: entry.sourceCardName,
      triggerOn: entry.triggerOn,
      chargesRemaining: newCharges,
      exhausted,
    } as JsonValue,
  });

  return { newState, log: nextLog, exhausted };
}

// ---------------------------------------------------------------------------
// getTriggersByOwner
// ---------------------------------------------------------------------------

export function getTriggersByOwner(
  state: TriggerState,
  ownerSeat: number,
): ReadonlyArray<TriggerInstance> {
  assertFiniteInt(ownerSeat, "getTriggersByOwner ownerSeat");
  return Object.freeze(state.entries.filter((e) => e.ownerSeat === ownerSeat));
}
